import { Modality } from 'src/types/api/data/image';
import {
  JobListSchema,
  JobState,
  JobType,
  JobAssignRequest,
} from 'src/types/api/data/job';
import { dataApi } from 'src/utils/http';

type JobAssignmentMutationParams = {
  projectId: string;
  userId: string;
  count: number;
  modality: Modality;
};

export const assignJobsByProject = async ({
  projectId,
  userId,
  count,
  modality,
}: JobAssignmentMutationParams): Promise<JobAssignRequest> => {
  const payload = { count };
  const { data } = await dataApi.patch<JobAssignRequest>(
    `/projects/${projectId}/jobs/assign/`,
    payload,
    {
      params: {
        modality: modality.toLowerCase(),
        user: userId,
      },
    }
  );

  return data;
};

export const unassignJobsByProject = async ({
  projectId,
  userId,
  count,
  modality,
}: JobAssignmentMutationParams): Promise<JobAssignRequest> => {
  // Fetch jobs assigned to the user to determine specific job IDs to unassign
  const jobsResponse = await getJobs({
    projectId,
    page: 1,
    pageSize: count,
    modality,
    annotatorId: userId,
    // Consider jobs that are currently assigned to the user
    states: ['assigned', 'queued'] as JobState[],
  });

  const jobIds = jobsResponse.jobs.map(j => j.id).slice(0, count);

  if (jobIds.length === 0) {
    return { success: [], failure: [] };
  }

  const results = await Promise.allSettled(
    jobIds.map(jobId =>
      dataApi.patch(`/v3/jobs/${jobId}/unassign`, undefined, {
        params: { modality: modality.toLowerCase() },
      })
    )
  );

  const success: string[] = [];
  const failure: string[] = [];
  results.forEach((res, idx) => {
    const id = jobIds[idx];
    if (!id) return;
    if (res.status === 'fulfilled') success.push(id);
    else failure.push(id);
  });

  return { success, failure };
};

export type getJobsProps = {
  projectId: string;
  page: number;
  pageSize: number;
  filter?: string;
  modality: Modality;
  annotatorId?: string;
  type?: JobType;
  states: JobState[];
};

// Get jobs of project
export const getJobs = async ({
  projectId,
  page,
  pageSize,
  filter,
  modality,
  annotatorId,
  type,
  states,
}: getJobsProps): Promise<JobListSchema> => {
  const { data } = await dataApi.get<JobListSchema>(
    `/v2/projects/${projectId}/jobs/`,
    {
      params: {
        project: projectId,
        page,
        page_size: pageSize,
        modality: modality.toLowerCase(),
        annotator: annotatorId,
        filter,
        type,
        states,
      },
    }
  );

  return data;
};
